import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds,MobileUnderCalAds } from "../../Ads";
const TITLE = "Calculatrice de loi d'Ohm - Calculez la tension, le courant ou la résistance";
const DESCRIPTION =
  "Calculatrice de loi d'Ohm - Calculez la tension, le courant ou la résistance. Calculateur de physique en francais avec resultat rapide, unites claires et aide pratique pour exercices et revisions.";
const KEYWORDS =
  "ohms law, calculatrice physique, formule physique, exercices de physique, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/ohmslaw";
function OhmsLawCalculator() {
  const [voltage, setVoltage] = useState("");
  const [current, setCurrent] = useState("");
  const [resistance, setResistance] = useState("");
  const [calculationMode, setCalculationMode] = useState("calculateVoltage");
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const voltageValue = parseFloat(voltage) || 0;
    const currentValue = parseFloat(current) || 0;
    const resistanceValue = parseFloat(resistance) || 0;
    switch (calculationMode) {
      case "calculateVoltage":
        if (currentValue > 0 && resistanceValue > 0) {
          const calculatedVoltage = currentValue * resistanceValue;
          setVoltage(Math.round(calculatedVoltage * 100) / 100);
        } else {
          setError("Veuillez entrer le courant et la résistance correctement");
        }
        break;
      case "calculateCurrent":
        if (voltageValue > 0 && resistanceValue > 0) {
          const calculatedCurrent = voltageValue / resistanceValue;
          setCurrent(Math.round(calculatedCurrent * 100) / 100);
        } else {
          setError("Veuillez entrer la tension et la résistance correctement");
        }
        break;
      case "calculateResistance":
        if (voltageValue > 0 && currentValue > 0) {
          const calculatedResistance = voltageValue / currentValue;
          setResistance(Math.round(calculatedResistance * 100) / 100);
        } else {
          setError("Veuillez entrer la tension et le courant correctement");
        }
        break;
      default:
        break;
    }
  }, [voltage, current, resistance, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handleVoltageChange = (e) => setVoltage(e.target.value);
  const handleCurrentChange = (e) => setCurrent(e.target.value);
  const handleResistanceChange = (e) => setResistance(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculateVoltage":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de la tension :</h4>
            <ol>
              <li>Entrez le courant (en Ampères) dans le champ dédié.</li>
              <li>Entrez la résistance (en Ohms) dans le champ dédié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir la tension.</li>
              <li>La tension est égale au courant × résistance.</li>
            </ol>
          </div>
        );
      case "calculateCurrent":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul du courant :</h4>
            <ol>
              <li>Entrez la tension (en Volts) dans le champ dédié.</li>
              <li>Entrez la résistance (en Ohms) dans le champ dédié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir le courant.</li>
              <li>Le courant est égal à la tension / résistance.</li>
            </ol>
          </div>
        );
      case "calculateResistance":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de la résistance :</h4>
            <ol>
              <li>Entrez la tension (en Volts) dans le champ dédié.</li>
              <li>Entrez le courant (en Ampères) dans le champ dédié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir la résistance.</li>
              <li>La résistance est égale à la tension / courant.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  La calculatrice de loi d'Ohm vous permet d'effectuer trois types de calculs en fonction des valeurs entrées. Choisissez le type de calcul approprié et entrez les valeurs disponibles pour calculer la valeur manquante avec précision et facilité.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Trouver la tension en connaissant le courant et la résistance
                </h2>
                <p>
                  Si vous connaissez le courant (en Ampères) et la résistance (en Ohms), vous pouvez calculer la tension (en Volts) en utilisant la formule suivante :
                </p>
                <p>
                  <b>Tension = Courant × Résistance</b>
                </p>
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE} </h2>
                </div>
                <hr />
                <div dir="ltr">
                  <div>
                    <div className="form-group">
                      <b>
                        <label>Mode de calcul :</label>
                      </b>
                      <select
                        value={calculationMode}
                        onChange={handleModeChange}
                        className="form-control"
                      >
                        <option value="calculateVoltage">Calculer la tension</option>
                        <option value="calculateCurrent">Calculer le courant</option>
                        <option value="calculateResistance">Calculer la résistance</option>
                      </select>
                    </div>
                    <br />
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">Tension (V)</label>
                      </b>
                      <input
                        type="number"
                        placeholder="0"
                        value={voltage}
                        onChange={handleVoltageChange}
                        className="form-control"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <br />
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">Courant (A)</label>
                      </b>
                      <input
                        type="number"
                        placeholder="0"
                        value={current}
                        onChange={handleCurrentChange}
                        className="form-control"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <br />
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">Résistance (Ω)</label>
                      </b>
                      <input
                        type="number"
                        placeholder="0"
                        value={resistance}
                        onChange={handleResistanceChange}
                        className="form-control"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <br />
                    <button
                      className="btn btn-success"
                      style={{ fontSize: "20px" }}
                      onClick={handleCalculateClick}
                    >
                      Calculer
                    </button>
                  </div>
                  <br />
                  <div>
                    {error !== "" ? (
                      <p className="p-3 mb-2 bg-warning text-white">
                        Erreur : {error}
                      </p>
                    ) : null}
                    {showSteps && getSolutionSteps()}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
      {" "}
      <br />
    </div>
  );
}
export default OhmsLawCalculator;
